import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Moon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, errMsg, useSite } from "@/lib/site";
import { toast } from "sonner";

const KEY = "cg_chat";
const PANEL_HIDDEN = { opacity: 0, y: 16, scale: 0.97 };
const PANEL_SHOWN = { opacity: 1, y: 0, scale: 1 };
const PANEL_TRANSITION = { duration: 0.2 };

const StartForm = ({ onStart }) => {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onStart((await api.post("/chat/start", f)).data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const cls = "w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm outline-none focus:border-blue-500 focus:bg-white transition-colors";
  return (
    <form onSubmit={submit} className="p-4 space-y-2.5" data-testid="live-chat-start-form">
      <input required placeholder="Your name" value={f.name} onChange={set("name")} className={cls} data-testid="live-chat-name-input" />
      <input required type="email" placeholder="Email" value={f.email} onChange={set("email")} className={cls} data-testid="live-chat-email-input" />
      <input placeholder="Phone (optional)" value={f.phone} onChange={set("phone")} className={cls} data-testid="live-chat-phone-input" />
      <textarea required rows={3} placeholder="How can we help?" value={f.message} onChange={set("message")} className={`${cls} resize-none`} data-testid="live-chat-first-message-input" />
      <button disabled={busy} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white py-3 text-sm font-semibold transition-colors disabled:opacity-60" data-testid="live-chat-start-form-btn">
        {busy ? "Starting…" : "Start chat"}
      </button>
    </form>
  );
};

const Bubble = ({ m }) => {
  if (m.sender === "system")
    return <div className="mx-auto max-w-[90%] text-center text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="live-chat-system-msg">{m.text}</div>;
  const mine = m.sender === "visitor";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${mine ? "bg-blue-600 text-white rounded-br-md" : "bg-slate-100 text-slate-800 rounded-bl-md"}`} data-testid={mine ? "live-chat-visitor-msg" : "live-chat-admin-msg"}>
        {m.text}
      </div>
    </div>
  );
};

const Thread = ({ session, onReset }) => {
  const [msgs, setMsgs] = useState(session.messages || []);
  const [available, setAvailable] = useState(session.available);
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get(`/chat/${session.conversation_id}`, { params: { token: session.token } })
      .then((r) => { if (alive) { setMsgs(r.data.messages); setAvailable(r.data.available); } })
      .catch((e) => { if (e?.response?.status === 404) onReset(); });
    poll(); const t = setInterval(poll, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [session, onReset]);
  useEffect(() => { const el = end.current?.parentElement; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    const t = text; setText("");
    try { const r = await api.post(`/chat/${session.conversation_id}/messages`, { token: session.token, text: t }); setMsgs((m) => [...m, r.data]); }
    catch (err) { toast.error(errMsg(err)); setText(t); }
  };
  return (
    <>
      {!available && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-800 text-xs border-b border-amber-100" data-testid="live-chat-away-banner">
          <Moon className="w-3.5 h-3.5" /> We're away right now — we'll get back to you by email.
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-2.5" data-testid="live-chat-messages">
        {msgs.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 rounded-xl bg-slate-50 border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-blue-500" data-testid="live-chat-message-input" />
        <button className="grid place-items-center w-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors" data-testid="live-chat-message-send-btn" aria-label="Send"><Send className="w-4 h-4" /></button>
      </form>
    </>
  );
};

export default function ChatWidget() {
  const { available } = useSite();
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(() => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } });
  const start = (s) => { localStorage.setItem(KEY, JSON.stringify({ conversation_id: s.conversation_id, token: s.token })); setSession(s); };
  const reset = useRef(() => { localStorage.removeItem(KEY); setSession(null); }).current;

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60]">
      <AnimatePresence>
        {open && (
          <motion.div initial={PANEL_HIDDEN} animate={PANEL_SHOWN} exit={PANEL_HIDDEN} transition={PANEL_TRANSITION}
            className="absolute bottom-[72px] right-0 w-[calc(100vw-2rem)] sm:w-[380px] h-[min(560px,calc(100vh-120px))] flex flex-col rounded-3xl bg-white border border-slate-200 shadow-[0_24px_60px_rgba(15,23,42,0.18)] overflow-hidden"
            data-testid="live-chat-panel">
            <div className="bg-[#090D16] text-white px-5 py-4 flex items-center justify-between">
              <div>
                <p className="font-display font-bold">Chat with Click & Grow</p>
                <p className="text-xs text-slate-400 flex items-center gap-1.5 mt-0.5" data-testid="live-chat-status">
                  <span className={`w-2 h-2 rounded-full ${available ? "bg-emerald-400" : "bg-amber-400"}`} />
                  {available ? "We usually reply in a few minutes" : "Away — we'll reply by email"}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="live-chat-close-btn" aria-label="Close"><X className="w-4 h-4" /></button>
            </div>
            {session ? <Thread session={session} onReset={reset} /> : (
              <div className="flex-1 overflow-y-auto">
                <p className="px-4 pt-4 text-sm text-slate-600">Questions about a package or a project? Leave your details and ask away.</p>
                <StartForm onStart={start} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setOpen(!open)} data-testid="floating-chat-toggle-btn" aria-label="Open chat"
        className="grid place-items-center w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white ring-4 ring-white shadow-[0_12px_30px_rgba(37,99,235,0.45)] transition-[transform,background-color] duration-200 hover:scale-105">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </div>
  );
}
